import { useParams } from 'next/navigation';
import classNames from 'classnames';
import Link from 'components/gruenland/atoms/link';
import Typography from 'components/gruenland/atoms/typography';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import {
  TYPOGRAPHY_PDP_VARIANT_NAME_LABEL,
  TYPOGRAPHY_PRODUCT_DETAIL_DISCOUNT,
  TYPOGRAPHY_PRODUCT_DETAIL_ORIGINAL_PRICE,
  TYPOGRAPHY_PRODUCT_DETAIL_PRICE,
  TYPOGRAPHY_PRODUCT_DETAIL_TITLE,
} from 'lib/typography';
import ColorPicker from '../ColorPicker';
import { ProductDetailsProps } from '../ProductDetails';
import SizePicker from '../SizePicker';

type ProductInformationProps = Pick<ProductDetailsProps, 'onChangeVariant' | 'product' | 'variant'>;

const ProductInformation: React.FC<ProductInformationProps> = ({ onChangeVariant, product, variant }) => {
  const { locale } = useParams();

  const { formatMessage } = useFormat({ name: 'gruenland' });

  const discountPercentage =
    variant.discountedPrice &&
    (((variant.price?.centAmount as number) - (variant.discountedPrice.centAmount as number)) /
      (variant.price?.centAmount as number)) *
      100;

  return (
    <div>
      <Typography as="h1" className={TYPOGRAPHY_PRODUCT_DETAIL_TITLE}>
        {product?.name}
      </Typography>

      <div className="mt-5">
        {discountPercentage ? (
          <div className="flex flex-row justify-between">
            <div className="flex items-end gap-2">
              <Typography className={TYPOGRAPHY_PRODUCT_DETAIL_PRICE}>
                {CurrencyHelpers.formatForCurrency(variant.discountedPrice as number, locale)}
              </Typography>
              <Typography className={TYPOGRAPHY_PRODUCT_DETAIL_ORIGINAL_PRICE}>
                {CurrencyHelpers.formatForCurrency(variant.price ?? 0, locale)}
              </Typography>
              <Typography
                className={TYPOGRAPHY_PRODUCT_DETAIL_DISCOUNT}
              >{`-${Math.round(discountPercentage)}%`}</Typography>
            </div>
          </div>
        ) : (
          <Typography className={TYPOGRAPHY_PRODUCT_DETAIL_PRICE}>
            {CurrencyHelpers.formatForCurrency(variant.price as number, locale)}
          </Typography>
        )}
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div>
          <p className={classNames(TYPOGRAPHY_PDP_VARIANT_NAME_LABEL, 'mb-[14px] uppercase')}>
            {formatMessage({ id: 'pdp.color', defaultMessage: 'Colore' })}
          </p>
          <ColorPicker currentVariant={variant} variants={product.variants} onClick={onChangeVariant} />
        </div>
        <div>
          <div className="mb-[14px] flex items-center justify-between">
            <p className={classNames(TYPOGRAPHY_PDP_VARIANT_NAME_LABEL, 'uppercase')}>
              {formatMessage({ id: 'pdp.size', defaultMessage: 'Taglia' })}
            </p>
            <Link
              link="/guida-taglie"
              className={classNames(TYPOGRAPHY_PDP_VARIANT_NAME_LABEL, 'underline')}
              target="_blank"
            >
              {formatMessage({ id: 'pdp.sizeGuide', defaultMessage: 'Guida alle misure' })}
            </Link>
          </div>
          <SizePicker currentVariant={variant} variants={product.variants} onClick={onChangeVariant} />
        </div>
      </div>
    </div>
  );
};

export default ProductInformation;
